import { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ValueFormatterParams } from "ag-grid-enterprise";
import { Button, IconButton, InputLabel, Tooltip } from "@mui/material";
import { Col, Form, Row } from "react-bootstrap";
import { getUserActivityData } from "../../api/userActivityApi/userAcitivtyApi";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import "../../config/colors.css";
import { UserAcitivtyRequestData } from "../../types/UserActivityRequestData";
import { useLoader } from "../../context/LoaderContext";

import ExcelIcon from "../../assets/images/ExcelExport.png";
import moment from "moment";
import { appSettings } from "../../config/appSetting";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import PushPinIcon from "@mui/icons-material/PushPin";
import { UserGridPreferenceModel } from "../../types/UserGridPreferenceModel";
import { useSnackbar } from "../Common/SnackbarContext";
import {
  getUserGridPreference,
  saveUserGridPreference,
} from "../../api/userDataApi/userGridPreferenceAPI";
import { FormatDate } from "../Common/CommonMethods";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";

const UserActivity: React.FC = () => {
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();
  const gridRef = useRef<AgGridReact>(null);
  const { show: isLoading,  setLoading } = useLoader();
  const [gridRecordExist, setGridRecordExist] = useState(false);
  const { showSnackbar } = useSnackbar();

  //Page load data
  useEffect(() => {
    getData(null);
  }, []);

  //Rename the columns
  const renameColumns = (val: any) => {
    if (val !== undefined) {
      if (val.toLowerCase() === "projectreference") {
        return "Project ID";
      } else if (
        val.toLowerCase() === "projectdocumentid" ||
        val.toLowerCase() === "project_id"
      ) {
        return "Document ID";
      } else if (val.toLowerCase() === "displayname") {
        return "Display Name";
      } else if (val.toLowerCase() === "level 1") {
        return "Rendition";
      } else if (
        val.toLowerCase() === "uploadby" ||
        val.toLowerCase() === "username"
      ) {
        return "User Name";
      } else if (val.toLowerCase() === "uploadedon") {
        return "Uploaded On";
      } else if (val === "actioN CODE") {
        return "Action Code";
      } else if (val.toLowerCase() === "ngfamily_id") {
        return "Family_Id";
      } else if (val.toLowerCase() === "statement_date") {
        return "Date";
      } else if (val.toLowerCase() === "sub_dob") {
        return "DateOfBirth";
      } else if (val.toLowerCase() === "sub_name") {
        return "Name";
      } else if (val.toLowerCase() === "lastmodifieddate") {
        return "Last Modified Date";
      } else if (val.toLowerCase() === "activityname") {
        return "Activity Name";
      } else if (val.toLowerCase() === "activitydatetime") {
        return "Activity Date";
      } else if (val.toLowerCase() === "filename") {
        return "File Name";
      } else if (val.toLowerCase() === "queuetype") {
        return "Queue Type";
      } else if (val.toLowerCase() === "documentstatus") {
        return "Document Status";
      } else if (val.toLowerCase() === "createddate") {
        return "Created Date";
      } else if (val.toLowerCase() === "documentid") {
        return "Document ID";
      } else {
        return val.replace(/&amp;/, "&");
      }
    }
  };

  //Restricted Columns
  const RestrictedColumns = [
    "documentStreamId",
    "description",
    "preWorkdocumentId",
    "projectDocumentId",
    "contextName",
    "timezone",
    "fromDate",
    "toDate",
    "noOfRecords",
    "currentPage",
    "sortColumn",
    "sortOrder",
    "totalRecords",
    "isexporttpExcel",
  ];
  function getMMDDYYYY(date: Date) {
    let today = new Date(date);
    let dd = today.getDate();
    let mm = today.getMonth() + 1;
    let yyyy = today.getFullYear();
    let formatdd = "";
    let formatmm = "";
    if (dd < 10) {
      formatdd = "0" + dd;
    } else {
      formatdd = dd.toString();
    }
    if (mm < 10) {
      formatmm = "0" + mm;
    } else {
      formatmm = mm.toString();
    }
    return formatmm + "-" + formatdd + "-" + yyyy;
  }
  //Get data from Api and set column headers dynamically
  const getData = async (params: any) => {
    try {
      setLoading(true);
      const payload: UserAcitivtyRequestData = {
        Fromdate: startDate ?  getMMDDYYYY(startDate) : '',
        Timezone: "UTC",
        Todate: getMMDDYYYY(new Date()),
        isExporttpExcel: true,
        sortColumn: "",
        currentPage: 0,
        contextName: (LocalStorageUtil.getItem("contextName") ?? "").toString(),
        noOfRecords: 0,
        sortOrder: 0,
      };
      if (params) {
        payload.Fromdate = params.Fromdate;
        payload.Todate = params.Todate;
      }
      if (params.Fromdate && params.Todate) {
        const response = await getUserActivityData(payload);
        if (response) {
          setGridRecordExist(true);
          setRowData(response);
          // Set grid preferences
          const gridPreferences = await getUserGridPreferences();
          if (gridPreferences) {
            applyGridPreferences(gridPreferences);
            showSnackbar("Grid state applied successfully.");
          }

          // Dynamically get column headers from the data
          const dynamicColumns = Object.keys(response[0])
            .filter((key) => !RestrictedColumns.includes(key))
            .map((key) => ({
              headerName: renameColumns(
                key.charAt(0).toUpperCase() + key.slice(1)
              ),
              field: key,
              sortable: true,
              filter: true,
              enableRowGroup: true,
              valueFormatter: key.toLowerCase().includes("date") // Check if the field is a date field
                ? (parameter: ValueFormatterParams) => {
                    if (!parameter.value) return "";

                    return FormatDate(parameter.value);
                  }
                : undefined, // Use default formatting if it's not a date field
            }));

          const combinedColumnDefs = [
            {
              headerName: "#",
              width: 70,
              suppressSizeToFit: true,
              colId: "colRowNum",
              sortable: false,
              filter: false,
              resizable: false,
              enableRowGroup: false,
              valueGetter: (params: any) => {
                return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
              },
            },
            ...dynamicColumns,
          ];
          setColumnDefs(combinedColumnDefs);
        }
      } else {
        setGridRecordExist(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const handleApplyButton = () => {
    if (startDate && endDate && startDate > endDate) {
      showSnackbar("From date should be less than To date", true);
    } else {
      const parameter: UserAcitivtyRequestData = {
        Fromdate: startDate ?  getMMDDYYYY(startDate) : '',
        Todate: getMMDDYYYY(endDate ?? new Date()),
        Timezone: "",
        contextName: "",
        currentPage: 0,
        isExporttpExcel: false,
        noOfRecords: 0,
        sortColumn: "",
        sortOrder: 0,
      };
      getData(parameter);
    }
  };

  //handel reset button
  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setRowData([]);
  };

  //#region Quick Filter
  //Searching
  const onFilterTextBoxChanged = useCallback(() => {
    debugger;
    const filterText = (
      document.getElementById("filter-text-box") as HTMLInputElement
    ).value;

    gridRef.current?.api.setGridOption("quickFilterText", filterText);
  }, []);
  //#endregion

  //Handle Export to excel
  const handleExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsExcel({
        fileName:
          "UserActivityReports_" + moment().format("YYYYMMDD") + ".xlsx",
        sheetName: "UserActivity Report",
        columnKeys: columnDefs
          .map((colDef) => colDef.field)
           .filter((field): field is string => typeof field === "string"), // NOSONAR
        processCellCallback: function (params) {
          return params.value;
        },
      });
    }
  };

  // #region Grid Preferences
  //Reapply grid state on component update
  useEffect(() => {
    const reapplyGridState = async () => {
      const gridPreferences = await getUserGridPreferences();
      if (gridPreferences) {
        applyGridPreferences(gridPreferences);
      }
    };
    reapplyGridState();
  }, [rowData]);

  //Save grid preferences
  const handleSaveGridPreferences = async () => {
    if (gridRef.current) {
      const gridState = gridRef.current.api.getColumnState();
      const userGridPreference: UserGridPreferenceModel = {
        Area: "UserActivity",
        UserID: Number(LocalStorageUtil.getItem("userId")),
        BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
        ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
        GridBlob: JSON.stringify(gridState),
        LastModifiedBy: "DocDistro-UserGridPreferencesAPI",
        LastModifiedDate: new Date().toISOString(),
      };
      try {
        const workInProgressModelInfo = await saveUserGridPreference(
          userGridPreference
        );
        if (workInProgressModelInfo) {
          showSnackbar("Grid state successfully saved.");
        }
      } catch (error) {
        console.error("Error fetching data", error);
      }
    }
  };

  //Get grid preferences
  const getUserGridPreferences = async () => {
    const userGridPreference = {
      Area: "UserActivity",
      UserID: Number(LocalStorageUtil.getItem("userId")),
      BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };
    try {
      const downstreamSummaryModelInfo = await getUserGridPreference(
        userGridPreference
      );
      return downstreamSummaryModelInfo;
    } catch (error) {
      showSnackbar("Error fetching data", true);
    } finally {
      setLoading(false);
    }
  };

  //Apply grid preferences
  const applyGridPreferences = (gridPreferences: any) => {
    if (gridPreferences) {
      try {
        const gridState = JSON.parse(gridPreferences);
        if (gridRef.current) {
          gridRef.current.api.applyColumnState({
            state: gridState,
            applyOrder: true,
          });

          gridRef.current.api.setFilterModel(gridState);
          gridRef.current.api.applyColumnState(gridState);
        }
      } catch (error) {
        console.error("Error parsing grid preferences:", error);
      }
    } else {
      console.warn("No grid preferences found or GridBlob is undefined.");
    }
  };
  // #endregion

  //Render the component
  return (
    <div>
      <NavigationBar title="UserActivityReport" text="User Activity" />
      <Row style={{ margin: "1em" }}>
        <Col>
          <div>
            <div
              className="col-sm-12"
              style={{
                display: "flex",
                gap: "5px",
                alignItems: "center",
                margin: ".5em",
              }}
            >
              {/* Dates, Apply and Reset buttons */}
              <div
                className="col-sm-8"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <InputLabel
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                    color: "var(--text-color)",
                    display: "inline-block !important",
                    marginBottom: "5px !important",
                    fontWeight: 600,
                    fontSize: "11px",
                  }}
                >
                  From Date: *
                </InputLabel>
                <Form.Control
                  type="date"
                  placeholder="From Date"
                  name="fromdate"
                  max={new Date().toISOString().split("T")[0]}
                  value={startDate ? startDate.toISOString().split("T")[0] : ""}
                  onChange={(e) => setStartDate(new Date(e.target.value))}
                  style={{
                    padding: ".3em",
                    width: "15%",
                    fontSize: "11px",
                    borderRadius: "0",
                    border: "1px solid #ccc",
                    borderColor: "black",
                    marginTop: ".1em",
                  }}
                />
                <InputLabel
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                    color: "var(--text-color)",
                    display: "inline-block !important",
                    marginBottom: "5px !important",
                    fontWeight: 600,
                    fontSize: "11px",
                  }}
                >
                  To Date: *
                </InputLabel>
                <Form.Control
                  type="date"
                  placeholder="To Date"
                  name="todate"
                  value={endDate ? endDate.toISOString().split("T")[0] : ""}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setEndDate(new Date(e.target.value))}
                  style={{
                    padding: ".3em",
                    width: "15%",
                    fontSize: "11px",
                    borderRadius: "0",
                    border: "1px solid #ccc",
                    borderColor: "black",
                    marginTop: ".1em",
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyButton}
                  className="button apply-button"
                >
                  Apply
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleReset}
                  className="button reset-button"
                >
                  Reset
                </Button>
              </div>

              {/* Grid Preferences and export to excel */}
              <div
                className="col-sm-4"
                style={{
                  display: "flex",
                  alignItems: "center",
                  content: "flex-end",
                  justifyContent: "flex-end",
                  width: "32.5%",
                  gap: ".1em",
                }}
              >
                {" "}
                {gridRecordExist && (
                  <>
                    <input
                      type="text"
                      id="filter-text-box"
                      placeholder="Quick Filter..."
                      onChange={onFilterTextBoxChanged}
                      style={{
                        width: "25em",
                        height: "2.5em",
                        fontSize: "11px",
                      }}
                    />
                    <Tooltip title="Click to save grid state">
                      <IconButton
                        onClick={handleSaveGridPreferences}
                        className="preference-icon"
                      >
                        <PushPinIcon />
                      </IconButton>
                    </Tooltip>
                    {isPagePermission("Allow Expert Excel","UserActivity",1453)&&(<Tooltip title="Click to export the grid content in excel">
                      <button
                        type="button"
                        onClick={handleExport}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                        aria-label="Export to Excel"
                      >
                        <img
                          src={ExcelIcon}
                          alt="Doc-Distro"
                          className="excel-icon"
                          style={{ marginRight: "1em" }}
                        />
                      </button>
                    </Tooltip>)}
                  </>
                )}
              </div>
            </div>
            <div className="queue-table-view clearfix">
              <div
                className="ag-theme-quartz-dark"
                style={{
                  height: "66vh",
                  marginTop: "1em",
                }}
              >
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={rowData}
                  pagination={true}
                  rowGroupPanelShow="always"
                  pivotPanelShow="always"
                  gridOptions={{
                    rowSelection: { 
                      mode : "multiRow",
                      checkboxes: true,
                      headerCheckbox: true,
                      enableClickSelection: false,
                     },  
                  }}
                  ref={gridRef}
                  headerHeight={30}
                  paginationPageSize={defaulPageSize}
                  paginationPageSizeSelector={pageSizeSelector}
                  loading={isLoading} 
                  overlayNoRowsTemplate={
                    '<span class="ag-overlay-loading-center">No rows to show</span>'
                  }
                  loadingOverlayComponent={CustomLoadingOverlay}
                  onGridReady={async (params) => {
                    const gridPreferences = await getUserGridPreferences();
                    if (gridPreferences) {
                      applyGridPreferences(gridPreferences);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default UserActivity;
